import { z } from 'zod';
import { ServerId } from './basics.js';
/**
 * The agent's request credential (knowledge doc section 10; decided in section 20).
 *
 * The agent signs a short-lived JWT with the same Ed25519 key pair the installer
 * generated, so no second secret exists. The API stores only the public key, which
 * makes revocation a row deletion rather than a certificate revocation list. Tokens
 * were chosen over mutual TLS because they survive any TLS-terminating proxy.
 *
 * This file is the contract both sides compile against: the API verifies against it
 * (`packages/api/src/auth`) and the agent mints against it (P1.5's `core/identity`).
 */
/** Ed25519. Nothing else is accepted, so `alg: none` and HMAC confusion cannot apply. */
export const AGENT_TOKEN_ALG = 'EdDSA';
/** Section 10 caps `exp` at five minutes out; a longer-lived token is refused. */
export const AGENT_TOKEN_MAX_LIFETIME_SECONDS = 300;
/** Section 10's tolerance, so a server whose clock drifts a little still works. */
export const AGENT_TOKEN_CLOCK_SKEW_SECONDS = 60;
/**
 * How long a `jti` must stay in the replay cache: the longest a token can be valid
 * plus the skew either side of it. Remembering for less would let a replay through.
 */
export const AGENT_TOKEN_REPLAY_WINDOW_SECONDS = AGENT_TOKEN_MAX_LIFETIME_SECONDS + 2 * AGENT_TOKEN_CLOCK_SKEW_SECONDS;
/**
 * Base64 of the agent's Ed25519 public key, in either the raw 32-byte form or its
 * 44-byte SPKI DER wrapper. Both are accepted because the two are distinguishable by
 * length and different key libraries emit different ones; the API normalizes to SPKI.
 *
 * A public key is not a secret, so it travels in the enrollment payload and is stored
 * as-is. Only the private half is protected, and it never leaves the server.
 */
export const AgentPublicKeyB64 = z
    .string()
    .min(32)
    .max(256)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'base64');
/**
 * Claims the API requires. Anything else in the payload is ignored rather than
 * rejected, so adding a claim later does not break agents that predate it.
 *
 * `iss` is the server id, `aud` is the API URL the token may be spent at, and `jti`
 * is the nonce the API caches. Making `jti` at least 16 characters keeps a guessable
 * nonce from colliding with a real one and evicting it.
 */
export const AgentTokenClaims = z.object({
    iss: ServerId,
    aud: z.string().min(1).max(512),
    jti: z.string().min(16).max(128),
    iat: z.number().int().positive(),
    exp: z.number().int().positive(),
});
//# sourceMappingURL=agent-token.js.map